import { atom, selector, RecoilState, RecoilValueReadOnly } from 'recoil';
import { componentVisibilityState } from './layout';

export type ComponentType = 'chat' | 'graph' | 'terminal';
export type DropZone = 'top-left' | 'left' | 'bottom-left' | 'top-right' | 'right' | 'bottom-right';

export interface ComponentPosition {
  zone: DropZone;
  order: number;
}

export interface LayoutPositions {
  chat: ComponentPosition;
  graph: ComponentPosition;
  terminal: ComponentPosition;
}

export interface DragState {
  isMoving: boolean;
  movingComponent: ComponentType | null;
  hoveredZone: DropZone | null;
  moveOffset: { x: number; y: number };
}

/**
 * Returns the default component positions
 * @returns Default layout positions for all components
 */
const getDefaultPositions = (): LayoutPositions => ({
  graph: { zone: 'top-left', order: 0 },
  terminal: { zone: 'bottom-left', order: 0 },
  chat: { zone: 'right', order: 0 }
});

/**
 * Loads component positions from localStorage with validation
 * Clears corrupted data and falls back to defaults if needed
 * @returns Valid component positions from localStorage or defaults
 */
const getSavedPositions = (): LayoutPositions => {
  try {
    const saved = localStorage.getItem('componentPositions');
    if (saved) {
      const parsed = JSON.parse(saved);
      
      const validZones: DropZone[] = ['top-left', 'left', 'bottom-left', 'top-right', 'right', 'bottom-right'];
      const isValidPosition = (position: any): position is ComponentPosition => {
        return position && 
               typeof position.zone === 'string' && 
               validZones.includes(position.zone as DropZone) &&
               typeof position.order === 'number';
      };
      
      const isValidLayout = (layout: any): layout is LayoutPositions => {
        return layout &&
               isValidPosition(layout.chat) &&
               isValidPosition(layout.graph) &&
               isValidPosition(layout.terminal);
      };
      
      if (isValidLayout(parsed)) {
        return parsed;
      } else {
        console.warn('Invalid component positions found in localStorage, clearing and using defaults');
        localStorage.removeItem('componentPositions');
        return getDefaultPositions();
      }
    }
  } catch (error) {
    console.warn('Failed to load component positions from localStorage:', error);
    localStorage.removeItem('componentPositions');
  }
  return getDefaultPositions();
};

/**
 * Atom for storing component positions with localStorage persistence
 */
export const componentPositionsState: RecoilState<LayoutPositions> = atom<LayoutPositions>({
  key: 'ComponentPositions',
  default: getSavedPositions(),
  effects: [
    ({ onSet }) => {
      let timeoutId: NodeJS.Timeout | null = null;
      onSet((newValue, oldValue) => {
        if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
          return;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        
        timeoutId = setTimeout(() => {
          try {
            localStorage.setItem('componentPositions', JSON.stringify(newValue));
          } catch (error) {
            console.warn('Failed to save component positions to localStorage:', error);
          }
          timeoutId = null;
        }, 100); // 100ms debounce
      });
    }
  ]
});

/**
 * Atom for storing drag state during component movement
 */
export const dragState: RecoilState<DragState> = atom<DragState>({
  key: 'DragState',
  default: {
    isMoving: false,
    movingComponent: null,
    hoveredZone: null,
    moveOffset: { x: 0, y: 0 }
  }
});

/**
 * Selector that groups all components by their zones, regardless of visibility
 * Includes validation to handle invalid zone data gracefully
 */
export const componentsByZoneSelector: RecoilValueReadOnly<Record<DropZone, ComponentType[]>> = selector({
  key: 'ComponentsByZone',
  get: ({ get }) => {
    const positions = get(componentPositionsState);
    const zones: Record<DropZone, ComponentType[]> = {
      'top-left': [],
      'left': [],
      'bottom-left': [],
      'top-right': [],
      'right': [],
      'bottom-right': []
    };

    (Object.keys(positions) as ComponentType[]).forEach(component => {
      const position = positions[component];
      if (position && position.zone && zones[position.zone]) {
        zones[position.zone].push(component);
      } else {
        console.warn(`Invalid zone "${position?.zone}" for component "${component}", resetting to default`);
        zones['right'].push(component);
      }
    });

    Object.keys(zones).forEach(zone => {
      zones[zone as DropZone].sort((a, b) => 
        positions[a].order - positions[b].order
      );
    });

    return zones;
  }
});

/**
 * Selector that groups only visible components by their zones
 * Filters out hidden components for layout calculations
 */
export const visibleComponentsByZoneSelector: RecoilValueReadOnly<Record<DropZone, ComponentType[]>> = selector({
  key: 'VisibleComponentsByZone',
  get: ({ get }) => {
    const allComponentsByZone = get(componentsByZoneSelector);
    const visibility = get(componentVisibilityState);
    
    const visibleZones: Record<DropZone, ComponentType[]> = {
      'top-left': [],
      'left': [],
      'bottom-left': [],
      'top-right': [],
      'right': [],
      'bottom-right': []
    };

    Object.keys(allComponentsByZone).forEach(zone => {
      visibleZones[zone as DropZone] = allComponentsByZone[zone as DropZone]
        .filter(component => visibility[component as keyof typeof visibility]);
    });

    return visibleZones;
  }
});


